import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import {
  useGetBusinessProfileQuery,
  useUpdateBusinessProfileMutation,
} from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import { IBusinessProfile } from "../../Api/Interface/api.interface";
import { uploadImage } from "../../Components/UploadFile/upload";
import EmailChipsInput from "../../Components/EmailChipsInput";
import { validateEmail } from "../../Components/Validation/validationUtils";

// ── Style constants (match Settings → Company so the section reads as one) ────
const fieldLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  letterSpacing: 0.4,
  mb: 0.75,
  display: "block",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 1.5,
    bgcolor: "#f8fafc",
    transition: "all 0.2s",
    "&:hover": { bgcolor: "#f1f5f9" },
    "&.Mui-focused": {
      bgcolor: "#ffffff",
      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#2563eb", borderWidth: 2 },
    },
  },
};

const sectionCard = {
  bgcolor: "#fff",
  borderRadius: 3,
  border: "1px solid #e2e8f0",
  mb: 3,
  overflow: "hidden",
  boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
};

const SectionHeading = ({
  icon,
  title,
  subtitle,
  color = "#2563eb",
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  color?: string;
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mb: 3 }}>
    <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: color + "18", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: 0.25 }}>
      <Box sx={{ color }}>{icon}</Box>
    </Box>
    <Box sx={{ borderLeft: `3px solid ${color}`, pl: 1.5 }}>
      <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#1e293b", lineHeight: 1.2 }}>{title}</Typography>
      <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>{subtitle}</Typography>
    </Box>
  </Box>
);

type LogoField = "main_logo" | "login_logo" | "email_logo" | "favicon";

const LOGO_FIELDS: { field: LogoField; label: string; hint: string }[] = [
  { field: "main_logo",  label: "Main / Portal Logo", hint: "Sidebar and portal header. Used for the others when they are empty." },
  { field: "login_logo", label: "Login Page Logo",    hint: "Falls back to the main logo." },
  { field: "email_logo", label: "Email Logo",         hint: "Header of enquiry emails. PNG or JPG — Outlook cannot show WEBP." },
  { field: "favicon",    label: "Favicon",            hint: "Browser tab icon. Square PNG, 64×64 or larger." },
];

const ToggleRow = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) => (
  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, p: 2, borderRadius: 2, bgcolor: "#f8fafc", border: "1px solid #e2e8f0", mb: 1.5 }}>
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>{label}</Typography>
      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>{description}</Typography>
    </Box>
    <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} />
  </Box>
);

/**
 * Settings → Business Profile.
 *
 * The TJ Bus identity: portal/login/email logos, public contact details,
 * social links and website-enquiry settings. Separate from Settings → Company,
 * which holds the billing profiles selected on invoices and quotes.
 */
const BusinessProfilePage = () => {
  const { showErrorMessage, showMessage } = useNotifier();
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  const { data, isLoading: loadingProfile } = useGetBusinessProfileQuery();
  const [saveProfile, { isLoading: saving }] = useUpdateBusinessProfileMutation();
  const [uploading, setUploading] = useState<LogoField | null>(null);

  const profile = useMemo(() => ((data as any)?.data || null) as IBusinessProfile | null, [data]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      business_name:   profile?.business_name   || "",
      legal_name:      profile?.legal_name      || "",
      short_name:      profile?.short_name      || "",
      tagline:         profile?.tagline         || "",
      email:           profile?.email           || "",
      phone:           profile?.phone           || "",
      alternate_phone: profile?.alternate_phone || "",
      website:         profile?.website         || "",
      address_line_1:  profile?.address_line_1  || "",
      address_line_2:  profile?.address_line_2  || "",
      city:            profile?.city            || "",
      province:        profile?.province        || "",
      postal_code:     profile?.postal_code     || "",
      country:         profile?.country         || "",
      main_logo:       profile?.main_logo       || "",
      login_logo:      profile?.login_logo      || "",
      email_logo:      profile?.email_logo      || "",
      favicon:         profile?.favicon         || "",
      facebook_url:    profile?.facebook_url    || "",
      instagram_url:   profile?.instagram_url   || "",
      linkedin_url:    profile?.linkedin_url    || "",
      youtube_url:     profile?.youtube_url     || "",
      twitter_url:     profile?.twitter_url     || "",
      tiktok_url:      profile?.tiktok_url      || "",
      admin_notification_emails: (profile?.admin_notification_emails || []) as string[],
      enquiry_reply_to_email:    profile?.enquiry_reply_to_email || "",
      website_lead_emails_enabled:      profile?.website_lead_emails_enabled ?? true,
      customer_acknowledgement_enabled: profile?.customer_acknowledgement_enabled ?? true,
      admin_notification_enabled:       profile?.admin_notification_enabled ?? true,
    },
    validate: (values) => {
      const errors: { [key: string]: string } = {};
      if (!values.business_name.trim()) errors.business_name = "Business name is required";
      const emailError = validateEmail(values.email);
      if (emailError) errors.email = emailError;
      const replyToError = validateEmail(values.enquiry_reply_to_email);
      if (replyToError) errors.enquiry_reply_to_email = replyToError;
      return errors;
    },
    onSubmit: async (values) => {
      try {
        const response = await saveProfile(values).unwrap();
        if (!response.status) showErrorMessage(response.message);
        else showMessage(response.message);
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  const previewUrl = (field: LogoField): string | undefined => {
    const stored = (formik.values as any)[field] as string;
    if (!stored) return undefined;
    return /^https?:\/\//i.test(stored) ? stored : `${LARAVEL_API_URL}/${stored}`;
  };

  const handleUpload = async (field: LogoField, file?: File) => {
    if (!file) return;
    const valid = ["image/jpeg", "image/png", "image/jpg", "image/gif", "image/webp"];
    if (!valid.includes(file.type)) { showErrorMessage("Must be jpeg, png, jpg, gif or webp."); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("File must be smaller than 2 MB."); return; }
    try {
      setUploading(field);
      const path = await uploadImage(file);
      formik.setFieldValue(field, path || "");
    } catch {
      showErrorMessage("Failed to upload the image.");
    } finally {
      setUploading(null);
    }
  };

  if (loadingProfile) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="40vh" flexDirection="column" gap={2}>
        <CircularProgress size={36} sx={{ color: "#2563eb" }} />
        <Typography sx={{ fontSize: 13, color: "#64748b" }}>Loading business profile...</Typography>
      </Box>
    );
  }

  const textField = (name: string, label: string, placeholder = "", adornment?: string) => (
    <>
      <Typography sx={fieldLabel}>{label}</Typography>
      <TextField
        size="small"
        fullWidth
        placeholder={placeholder}
        {...formik.getFieldProps(name)}
        error={Boolean((formik.touched as any)[name] && (formik.errors as any)[name])}
        helperText={(formik.touched as any)[name] && (formik.errors as any)[name]}
        sx={inputSx}
        InputProps={adornment ? {
          startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: 13, color: "#94a3b8" }}>{adornment}</Typography></InputAdornment>,
        } : undefined}
      />
    </>
  );

  return (
    <Box sx={{ bgcolor: "#f1f5f9", minHeight: "100vh" }}>
      <form onSubmit={formik.handleSubmit}>

        {/* ── Hero header ── */}
        <Box sx={{
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          px: { xs: 2, md: 4 }, py: 3,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexWrap: "wrap", gap: 2,
          boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2.5,
              background: "linear-gradient(135deg, #2563eb, #1d4ed8)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 12px rgba(37,99,235,0.4)",
            }}>
              <StorefrontOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#ffffff", lineHeight: 1.2 }}>
                Business Profile
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
                Portal, login and email branding — separate from invoice companies
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <SaveAltIcon />}
            disabled={saving || Boolean(uploading)}
            onClick={() => formik.handleSubmit()}
            sx={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", borderRadius: 2, fontWeight: 700, boxShadow: "0 4px 12px rgba(37,99,235,0.4)", "&:hover": { background: "linear-gradient(135deg, #1d4ed8, #1e40af)" } }}
          >
            Save Changes
          </Button>
        </Box>

        <Box sx={{ px: { xs: 2, md: 4 }, py: 4 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={8}>

              {/* ── General ── */}
              <Box sx={sectionCard}>
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading icon={<StorefrontOutlinedIcon sx={{ fontSize: 20 }} />} title="General Information" subtitle="How the business names itself" color="#2563eb" />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>{textField("business_name", "Business Name *", "e.g. TJ Bus")}</Grid>
                    <Grid item xs={12} sm={6}>{textField("legal_name", "Legal Name", "e.g. TJ Bus Lines Ltd")}</Grid>
                    <Grid item xs={12} sm={4}>{textField("short_name", "Short Name", "e.g. TJ Bus")}</Grid>
                    <Grid item xs={12} sm={8}>{textField("tagline", "Tagline", "e.g. Your Journey, Our Priority")}</Grid>
                  </Grid>
                </Box>
              </Box>

              {/* ── Contact ── */}
              <Box sx={sectionCard}>
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading icon={<ContactPhoneIcon sx={{ fontSize: 20 }} />} title="Contact Information" subtitle="Shown in the footer of every enquiry email" color="#0891b2" />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>{textField("email", "Main Email", "info@tjbus.ca", "✉️")}</Grid>
                    <Grid item xs={12} sm={6}>{textField("phone", "Main Phone", "(XXX) XXX-XXXX", "📞")}</Grid>
                    <Grid item xs={12} sm={6}>{textField("alternate_phone", "Alternate Phone", "(XXX) XXX-XXXX", "📱")}</Grid>
                    <Grid item xs={12} sm={6}>{textField("website", "Website", "https://www.tjbus.ca", "🌐")}</Grid>
                  </Grid>
                </Box>
              </Box>

              {/* ── Address ── */}
              <Box sx={sectionCard}>
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading icon={<LocationOnIcon sx={{ fontSize: 20 }} />} title="Address" subtitle="Public address for email footers" color="#16a34a" />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12} sm={6}>{textField("address_line_1", "Address Line 1", "123 Main Street")}</Grid>
                    <Grid item xs={12} sm={6}>{textField("address_line_2", "Address Line 2", "Unit 4")}</Grid>
                    <Grid item xs={12} sm={4}>{textField("city", "City", "Toronto")}</Grid>
                    <Grid item xs={12} sm={4}>{textField("province", "Province", "Ontario")}</Grid>
                    <Grid item xs={12} sm={4}>{textField("postal_code", "Postal Code", "M1A 1A1")}</Grid>
                    <Grid item xs={12} sm={6}>{textField("country", "Country", "Canada")}</Grid>
                  </Grid>
                </Box>
              </Box>

              {/* ── Website leads & enquiries ── */}
              <Box sx={sectionCard}>
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading icon={<MarkEmailReadOutlinedIcon sx={{ fontSize: 20 }} />} title="Website Leads & Enquiries" subtitle="Who is told, and which emails go out" color="#f59e0b" />
                  <Grid container spacing={2.5}>
                    <Grid item xs={12}>
                      <Typography sx={fieldLabel}>Admin Notification Emails</Typography>
                      <EmailChipsInput
                        value={formik.values.admin_notification_emails}
                        onChange={(emails) => formik.setFieldValue("admin_notification_emails", emails)}
                        sx={inputSx}
                      />
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                        Notified of every new website enquiry. If empty, the main email above is used.
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      {textField("enquiry_reply_to_email", "Reply-to Email", "enquiries@tjbus.ca", "↩️")}
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>
                        Where a customer's reply to the thank-you email goes. Defaults to the main email.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <ToggleRow
                        label="Website Lead Emails"
                        description="Master switch. Off means no enquiry email at all — enquiries are still saved."
                        checked={formik.values.website_lead_emails_enabled}
                        onChange={(v) => formik.setFieldValue("website_lead_emails_enabled", v)}
                      />
                      <ToggleRow
                        label="Customer Acknowledgement"
                        description="Send the thank-you email to the customer."
                        checked={formik.values.customer_acknowledgement_enabled}
                        onChange={(v) => formik.setFieldValue("customer_acknowledgement_enabled", v)}
                      />
                      <ToggleRow
                        label="Admin Notification"
                        description="Send the new-enquiry email to the addresses above."
                        checked={formik.values.admin_notification_enabled}
                        onChange={(v) => formik.setFieldValue("admin_notification_enabled", v)}
                      />
                      <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 1 }}>
                        The wording of both emails is edited under Settings → Email
                        ("Website Enquiry - Customer Acknowledgement" and "Website Enquiry - Admin Notification").
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>

            {/* ── Right column ── */}
            <Grid item xs={12} lg={4}>

              {/* ── Branding ── */}
              <Box sx={sectionCard}>
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading icon={<ImageOutlinedIcon sx={{ fontSize: 20 }} />} title="Branding" subtitle="Logos used across the portal and emails" color="#7c3aed" />
                  {LOGO_FIELDS.map(({ field, label, hint }) => {
                    const preview = previewUrl(field);
                    const inputId = `upload-${field}`;
                    return (
                      <Box key={field} sx={{ mb: 2.5 }}>
                        <Typography sx={fieldLabel}>{label}</Typography>
                        {uploading === field ? (
                          <Box sx={{ borderRadius: 2.5, border: "2px dashed #e2e8f0", p: 3, display: "flex", justifyContent: "center" }}>
                            <CircularProgress size={24} sx={{ color: "#7c3aed" }} />
                          </Box>
                        ) : preview ? (
                          <Box sx={{ borderRadius: 2.5, border: "2px solid #7c3aed40", bgcolor: "#7c3aed08", p: 2, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                            <img src={preview} alt={label} style={{ maxHeight: 64, maxWidth: "100%", objectFit: "contain" }} />
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <label htmlFor={inputId}>
                                <Button size="small" variant="outlined" component="span" startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                                  sx={{ fontSize: 11, borderColor: "#7c3aed80", color: "#7c3aed", borderRadius: 1.5, py: 0.4 }}>
                                  Replace
                                </Button>
                              </label>
                              <Button size="small" variant="outlined" startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
                                onClick={() => formik.setFieldValue(field, "")}
                                sx={{ fontSize: 11, borderColor: "#fca5a5", color: "#dc2626", borderRadius: 1.5, py: 0.4 }}>
                                Remove
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <label htmlFor={inputId} style={{ display: "block", cursor: "pointer" }}>
                            <Box sx={{
                              borderRadius: 2.5, border: "2px dashed #e2e8f0", bgcolor: "#f8fafc", p: 2.5,
                              display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
                              "&:hover": { borderColor: "#7c3aed", bgcolor: "#7c3aed08" },
                            }}>
                              <CloudUploadIcon sx={{ fontSize: 22, color: "#7c3aed" }} />
                              <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155" }}>Click to upload</Typography>
                            </Box>
                          </label>
                        )}
                        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.75 }}>{hint}</Typography>
                        <input accept="image/*" style={{ display: "none" }} id={inputId} type="file"
                          onChange={(e) => handleUpload(field, e.target.files?.[0])} />
                      </Box>
                    );
                  })}
                  <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                    Leave a logo empty to use the one built into the portal.
                  </Typography>
                </Box>
              </Box>

              {/* ── Social ── */}
              <Box sx={sectionCard}>
                <Box sx={{ px: 3, pt: 3, pb: 2.5 }}>
                  <SectionHeading icon={<ShareOutlinedIcon sx={{ fontSize: 20 }} />} title="Social Media" subtitle="Only the links you fill in appear in emails" color="#db2777" />
                  <Grid container spacing={2}>
                    <Grid item xs={12}>{textField("facebook_url", "Facebook", "https://facebook.com/yourpage", "📘")}</Grid>
                    <Grid item xs={12}>{textField("instagram_url", "Instagram", "https://instagram.com/yourpage", "📷")}</Grid>
                    <Grid item xs={12}>{textField("linkedin_url", "LinkedIn", "https://linkedin.com/company/...", "💼")}</Grid>
                    <Grid item xs={12}>{textField("youtube_url", "YouTube", "https://youtube.com/@...", "▶️")}</Grid>
                    <Grid item xs={12}>{textField("twitter_url", "X (Twitter)", "https://x.com/...", "✖️")}</Grid>
                    <Grid item xs={12}>{textField("tiktok_url", "TikTok", "https://tiktok.com/@...", "🎵")}</Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </form>
    </Box>
  );
};

export default BusinessProfilePage;
